import { isValidISODate } from '@/lib/dateUtils';
import type { TourBookingPolicy } from './tour-booking-policy';
import { isBeforeLeadCutoff, getMinTourContactDateISO } from '@/lib/tours/booking-lead-time';

/** Colombia no usa horario de verano: la salida se ancla a UTC−5. */
const BOGOTA_OFFSET = '-05:00';

/**
 * Fecha de «salida urgente»: todavía se puede pedir por WhatsApp (antes de la
 * hora de salida) pero cae dentro de la anticipación del pago online. Sin hora
 * conocida no hay urgencia: el contacto mínimo coincide con el pagable.
 */
export function isUrgentTourDate(p: {
  tourDate: string;
  startTime: string | null | undefined;
  bookingLeadDays: number;
  now?: Date;
  policy?: TourBookingPolicy;
}): boolean {
  if (!isValidISODate(p.tourDate)) return false;
  const now = p.now ?? new Date();
  const minContact = getMinTourContactDateISO(p.startTime, p.bookingLeadDays, now, p.policy);
  return p.tourDate >= minContact && isBeforeLeadCutoff(p.bookingLeadDays, p.tourDate, now, p.startTime ?? null, p.policy);
}

/** Milisegundos hasta la salida (fecha + hora de Bogotá); `null` sin hora. */
export function msUntilDeparture(
  tourDate: string,
  startTime: string | null | undefined,
  now: Date = new Date()
): number | null {
  const match = typeof startTime === 'string'
    ? startTime.trim().match(/^([01]\d|2[0-3]):([0-5]\d)/)
    : null;
  if (!match || !isValidISODate(tourDate)) return null;
  const departure = Date.parse(`${tourDate}T${match[1]}:${match[2]}:00${BOGOTA_OFFSET}`);
  return Math.max(0, departure - now.getTime());
}

/** «2 h 05 min» con una hora o más; «42:05» (min:seg) en la última hora. */
export function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours} h ${String(minutes).padStart(2, '0')} min`;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
